import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  effect,
  EventEmitter,
  inject,
  OnInit,
  Output,
  signal,
  Signal,
  viewChild,
  WritableSignal,
} from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import {
  AuthenticateErrorEnum,
  AuthenticateStateEnum,
  AuthService,
  CognitoSignInResult,
  SignInEnum,
  SignUpEnum,
} from "@keus-template/auth";
import { passwordMatchValidator } from "@keus-template/validators";
import { NgIcon, provideIcons } from "@ng-icons/core";
import { lucideCircleCheckBig, lucideLoaderCircle } from "@ng-icons/lucide";
import { BrnDialogComponent, BrnDialogContentDirective } from "@spartan-ng/brain/dialog";
import { HlmButtonDirective } from "@spartan-ng/ui-button-helm";
import { HlmDialogComponent, HlmDialogContentComponent } from "@spartan-ng/ui-dialog-helm";
import { QuestionnaireDialogComponent } from "../../../../../../components/molecules/questionnaire-dialog/questionnaire-dialog.component";
import { AuthFlowStatus } from "../../../../../../enums/auth-flow-status.enum";
import {
  AUTH_TAB_LABELS,
  AuthTab,
  getAuthTabFromString,
} from "../../../../../../enums/auth-tab.enum";
import { AuthFlowService } from "../../../../../../services/auth-flow/auth-flow.service";
import { LoginAndRegistrationFormComponent } from "../../molecules/login-and-registration-form/login-and-registration-form.component";
import { LoginAndRegistrationForm } from "../../molecules/login-and-registration-form/login-and-registration-form.interface";
import { TotpVerificationComponent } from "../../molecules/totp-verification/totp-verification.component";
import { HlmIconDirective } from "@spartan-ng/ui-icon-helm";

/**
 * The sign in and sign out component.
 */
@Component({
  selector: "keus-sign-in-and-sign-up",
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    LoginAndRegistrationFormComponent,
    TotpVerificationComponent,
    HlmButtonDirective,
    BrnDialogContentDirective,
    HlmDialogComponent,
    HlmDialogContentComponent,
    QuestionnaireDialogComponent,
    NgIcon,
    HlmIconDirective
  ],
  templateUrl: "./sign-in-and-sign-up.component.html",
  styleUrl: "./sign-in-and-sign-up.component.css",
  providers: [provideIcons({ lucideLoaderCircle, lucideCircleCheckBig })],
  host: {
    class: "block",
  },
})
export class SignInAndSignUpComponent implements OnInit {
  /** The authentication service. */
  private readonly _authService: AuthService = inject(AuthService);

  /** Router for navigation */
  private readonly _router: Router = inject(Router);

  /** Activated route for query params */
  private readonly _route: ActivatedRoute = inject(ActivatedRoute);

  /** Auth flow service */
  private readonly _authFlowService = inject(AuthFlowService);

  /** Auth tab enum for template */
  public readonly AuthTab = AuthTab;

  /** Auth tab labels for display */
  public readonly AUTH_TAB_LABELS = AUTH_TAB_LABELS;

  /** Query params as signal */
  private readonly _queryParams = toSignal(this._route.queryParams);

  /** Current tab from query params */
  private readonly _tabFromParams = computed(() => {
    const params = this._queryParams();
    const tabString = params?.["tab"] as string | undefined;
    return getAuthTabFromString(tabString);
  });

  /** Current tab signal */
  public readonly currentTab: WritableSignal<AuthTab> = signal(AuthTab.LOGIN);

  /** Convert AuthTab enum to form component expected values */
  public readonly activeFormTab = computed(() => {
    const tab = this.currentTab();
    return tab === AuthTab.REGISTER ? "registration" : "login";
  });

  /** The authentication authForm. */
  public readonly authForm: FormGroup<LoginAndRegistrationForm> =
    new FormGroup<LoginAndRegistrationForm>({
      email: new FormControl<string>("", {
        nonNullable: true,
        validators: [Validators.required, Validators.email],
      }),
      password: new FormControl<string>("", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(
            /(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[@$!%*#?&^_-]).{8,}/,
          ),
        ],
      }),
      confirmPassword: new FormControl<string | null>(null, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      privacyAccepted: new FormControl<boolean>(false, {
        nonNullable: true,
      }),
      marketingConsent: new FormControl<boolean>(false, {
        nonNullable: true,
      }),
    });

  /** Indicates whether the component is currently loading. */
  public readonly isLoading: WritableSignal<boolean> = signal(false);

  /** Error message */
  public readonly errorMessage: WritableSignal<string | undefined> = signal<
    string | undefined
  >(undefined);

  /** Indicates whether TOTP verification is required */
  public readonly showTotpVerification: WritableSignal<boolean> = signal(false);

  /** Event emitted when the user signs in. */
  @Output() public readonly signInEvent: EventEmitter<AuthenticateStateEnum> =
    new EventEmitter<AuthenticateStateEnum>();

  /** Dialog reference */
  public readonly viewchildDialogRef: Signal<BrnDialogComponent | undefined> = viewChild(BrnDialogComponent);

  constructor() {
    // Initialize tab from query params
    effect(() => {
      const tabFromParams = this._tabFromParams();
      this.currentTab.set(tabFromParams);
    });

    // Effect to sync tab changes with URL
    effect(() => {
      const tab = this.currentTab();
      const currentParams = this._queryParams();

      // Only update URL if the tab has actually changed
      if (currentParams?.["tab"] !== tab) {
        this._router.navigate([], {
          relativeTo: this._route,
          queryParams: { tab },
          queryParamsHandling: "merge",
          replaceUrl: true,
        });
      }
    });
  }

  /**
   * Handle tab change from UI
   * @param tab The new tab to set
   */
  public onTabChange(tab: AuthTab): void {
    this.currentTab.set(tab);
  }

  /**
   * Initialize the component.
   */
  ngOnInit() {
    this.authForm.controls.confirmPassword.setValidators([
      Validators.required,
      passwordMatchValidator(this.authForm.controls.password),
    ]);
    this.authForm.controls.confirmPassword.updateValueAndValidity();
  }

  /**
   * Initializes the component.
   */
  ngAfterViewInit(): void {
    if (this.activeFormTab() !== "login" && this._authFlowService.currentStatus() === AuthFlowStatus.REGISTRATION) {
      this.viewchildDialogRef()?.open();
    }
  }

  /**
   * Manages the sign-in and sign-up process.
   * @param type - The type of action to perform ("signin" or "signup").
   */
  public manageSigns(type: "signin" | "signup" | "google" | null): void {
    switch (type) {
      case "signin":
        this._signIn();
        break;
      case "signup":
        this._signUp();
        break;
      case "google":
        this._authService.signInWithGoogle();
        break;
    }
  }

  /**
   * Signs in the user.
   */
  private _signIn(): void {
    const { email, password } = this.authForm.value;
    if (!email || !password) return;

    this.errorMessage.set(undefined);
    this.authForm.setErrors(null);
    this.isLoading.set(true);

    this._authService.signIn$(email, password).subscribe({
      next: (result) => {
        switch (result.type) {
          case SignInEnum.NEW_PASSWORD_REQUIRED:
            this.signInEvent.emit(AuthenticateStateEnum.RESET_PASSWORD);
            break;
          case SignInEnum.TOTP_REQUIRED:
            this.isLoading.set(false);
            this.showTotpVerification.set(true);
            break;
          case SignInEnum.SUCCESS:
            this.signInEvent.emit(AuthenticateStateEnum.SIGN_IN_AND_SIGN_UP);
            break;
        }
      },
      error: (error: CognitoSignInResult) => {
        this.isLoading.set(false);
        const result = error.result as Error;
        if (
          result.name === AuthenticateErrorEnum.USER_NOT_CONFIRMED_EXCEPTION
        ) {
          this.signInEvent.emit(AuthenticateStateEnum.CONFIRM_SIGN_UP);
        } else if (
          result.name ===
          AuthenticateErrorEnum.PASSWORD_RESET_REQUIRED_EXCEPTION
        ) {
          this.signInEvent.emit(AuthenticateStateEnum.RESET_PASSWORD);
        } else {
          this.errorMessage.set(result.message);
          this.authForm.controls.email.setErrors({ incorrect: true });
          this.authForm.controls.password.setErrors({ incorrect: true });
        }
      },
      complete: () => this.isLoading.set(false),
    });
  }

  /**
   * Signs up a user.
   */
  private _signUp() {
    const { email, password } = this.authForm.value;
    if (!email || !password) return;

    this.isLoading.set(true);
    this._authService.signUp$(email, password).subscribe({
      next: (result) => {
        this.isLoading.set(false);
        if (result.type === SignUpEnum.SUCCESS) {
          this.signInEvent.emit(AuthenticateStateEnum.CONFIRM_SIGN_UP);
        }
      },
      error: () => {
        this.isLoading.set(false);
      },
      complete: () => this.isLoading.set(false),
    });
  }

  /**
   * Handles successful TOTP verification
   */
  public onTotpVerificationSuccess(result: CognitoSignInResult): void {
    this.showTotpVerification.set(false);
    if (result.type === SignInEnum.SUCCESS) {

      this.signInEvent.emit(AuthenticateStateEnum.SIGN_IN_AND_SIGN_UP);
    }
  }

  /**
   * Handles TOTP verification cancellation
   */
  public onTotpVerificationCancel(): void {
    this.showTotpVerification.set(false);
    this.isLoading.set(false);
  }

  /**
   * Handles reset password click
   */
  public onResetPasswordClick(): void {
    this.signInEvent.emit(AuthenticateStateEnum.RESET_PASSWORD);
  }

  /**
   * Handles back to questionnaire button click
   */
  public onBackToQuestionnaire(): void {
    // Use goBackToStep to properly clear session data and return to questionnaire
    this._authFlowService.goBackToStep(AuthFlowStatus.QUESTIONNAIRE);
  }

  /**
   * Handles back to questionnaire button click
   */
  public onBackToLogin(): void {
    // Use goBackToStep to properly clear session data and return to questionnaire
    this._router.navigate([], { relativeTo: this._route, queryParams: { tab: 'login' }, replaceUrl: true });
    this.showTotpVerification.set(false);
    this.isLoading.set(false);
  }
}
